import React from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Grid3X3,
  List,
  Search,
  RefreshCw,
  Package,
  Filter,
  Gem,
  Coins,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Capacitor } from '@capacitor/core';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useCurrencyBalance } from "@/hooks/use-currency";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import PullToRefresh from "react-simple-pull-to-refresh";

// Helper for card back image URLs
const getCardBackImageUrl = (imageUrl: string): string => {
  if (!imageUrl) return "/assets/images/default-card-back.svg";
  if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL && imageUrl.startsWith("/")) {
    return `${import.meta.env.VITE_SERVER_URL}${imageUrl}`;
  }
  return imageUrl;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100, damping: 12 } },
};

interface HeaderProps {
  readonly title: string;
  readonly backPath: string;
}

function EnhancedMarketplaceHeader({ title, backPath }: HeaderProps) {
  // ...existing code from deck-marketplace header...
  const [_, setLocation] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [safePaddingTop, setSafePaddingTop] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    const getSafeAreaInsets = async () => {
      if (Capacitor.isNativePlatform()) {
        setSafePaddingTop(50);
        const capacitorStatusBar = (window as any).Capacitor?.Plugins?.StatusBar;
        if (capacitorStatusBar?.getInfo) {
          try {
            capacitorStatusBar.getInfo().then((info: any) => {
              setSafePaddingTop(info?.height ? info.height + 5 : 50);
            });
          } catch {}
        }
      } else {
        setSafePaddingTop(16);
      }
    };
    window.addEventListener('scroll', handleScroll);
    getSafeAreaInsets();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const titleParts = title.split(' ');
  const firstWord = titleParts[0];
  const restOfTitle = titleParts.slice(1).join(' ');
  return (
    <div className={cn("sticky top-0 z-30 transition-all duration-300", scrolled ? "bg-black/90 backdrop-blur-xl shadow-lg" : "bg-gradient-to-b from-black/80 to-transparent")}
      style={{ paddingTop: safePaddingTop ? `${safePaddingTop}px` : undefined }}>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setLocation(backPath)} className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">
            <span className="text-white">{firstWord}</span>{" "}
            <span className="text-green-500">{restOfTitle}</span>
          </h1>
        </div>
      </div>
    </div>
  );
}

interface CardBack {
  id: number;
  name: string;
  description?: string;
  imageUrl: string;
  price?: number;
  currencyType: 'regular' | 'premium';
}

interface CardBackCardProps {
  readonly cardBack: CardBack;
  readonly onBuy: () => void;
  readonly onMagnify: () => void;
}

function CardBackCard({ cardBack, onBuy, onMagnify }: CardBackCardProps) {
  const price = Math.floor(parseFloat(cardBack.price?.toString() ?? "0"));
  const isFree = !cardBack.price || price === 0;
  
  return (
    <motion.div
      variants={itemVariants}
      className="group overflow-hidden rounded-lg bg-gradient-to-b from-gray-900/80 to-black/90 hover:from-gray-800/80 hover:to-black transition-all cursor-pointer h-full flex flex-col border border-primary/20 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
      onClick={onMagnify}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onMagnify();
        }
      }}
    >
      <div className="relative flex justify-center items-center p-4 bg-gradient-to-b from-black/40 to-transparent group-hover:scale-105 transition-transform duration-300">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <img 
          src={getCardBackImageUrl(cardBack.imageUrl)} 
          alt={cardBack.name} 
          className="w-24 h-32 object-contain drop-shadow-lg"
          onError={(e) => {
            const img = e.currentTarget;
            img.src = "/assets/images/default-card-back.svg";
          }}
        />
      </div>
      <div className="p-4 flex-1 flex flex-col justify-between border-t border-primary/10">
        <div>
          <h3 className="font-bold text-base text-white/90 line-clamp-1 group-hover:text-white transition-colors">{cardBack.name}</h3>
          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{cardBack.description}</p>
        </div>
        <div className="flex justify-between items-center mt-4 pt-3 border-t border-primary/10">
          <div className="flex items-center gap-1.5">
            {isFree ? (
              <Badge variant="secondary" className="bg-green-950/50 text-green-400 hover:bg-green-950/70">Free</Badge>
            ) : (
              <div className="flex items-center gap-1.5 bg-gray-900/50 px-2 py-1 rounded-full">
                {cardBack.currencyType === "premium" ? 
                  <Gem className="h-4 w-4 text-purple-400" /> : 
                  <Coins className="h-4 w-4 text-amber-400" />
                }
                <span className="text-sm font-medium">{price}</span>
              </div>
            )}
          </div>
          <Button 
            size="sm" 
            className={cn(
              "px-4 transition-all",
              isFree ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"
            )}
            onClick={(e) => {
              e.stopPropagation();
              onBuy();
            }}
          >
            {isFree ? "Add" : "Buy"}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

function LoadingCardBack() {
  return (
    <div className="rounded-lg bg-black/80 border border-primary/20 h-full flex flex-col">
      <div className="relative flex justify-center items-center p-4">
        <Skeleton className="w-24 h-32 bg-gray-800/50" />
      </div>
      <div className="p-4 flex-1 flex flex-col justify-between border-t border-primary/10">
        <div>
          <Skeleton className="h-5 w-3/4 bg-gray-800/50" />
          <Skeleton className="h-3 w-full mt-2 bg-gray-800/50" />
        </div>
        <div className="flex justify-between items-center mt-4 pt-3 border-t border-primary/10">
          <Skeleton className="h-8 w-16 bg-gray-800/50" />
          <Skeleton className="h-8 w-20 bg-gray-800/50" />
        </div>
      </div>
    </div>
  );
}

// Helper function to determine WebSocket protocol
function getWebSocketProtocol(url: string) {
  return (new URL(url)).protocol === "https:" ? "wss:" : "ws:";
}

export default function CardBackMarketplace() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setLocation] = useLocation();
  const { token } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [sortBy, setSortBy] = useState<"name" | "price">("name");
  const [viewType, setViewType] = useState<"grid" | "list">("grid");

  const { data: cardBacks, isLoading, refetch } = useQuery<CardBack[]>({
    queryKey: ["cardBacks-shop"],
    queryFn: async () => {
      let apiUrl = "/api/card-backs/shop";
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }
      const response = await fetch(apiUrl, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });
      if (!response.ok) throw new Error("Failed to fetch card backs");
      return response.json();
    },
  });

  const { data: balance } = useCurrencyBalance();
  const [magnified, setMagnified] = useState<CardBack | null>(null);
  const [buying, setBuying] = useState<CardBack | null>(null);
  const [isBuying, setIsBuying] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);

  const filteredAndSortedCardBacks = React.useMemo(() => {
    let result = cardBacks || [];
    
    // Filter by search
    if (searchQuery) {
      result = result.filter(cb => 
        cb.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cb.description?.toLowerCase()?.includes(searchQuery.toLowerCase())
      );
    }

    // Filter by tab
    if (activeTab === "premium") {
      result = result.filter(cb => cb.currencyType === "premium");
    } else if (activeTab === "standard") {
      result = result.filter(cb => cb.currencyType !== "premium");
    } else if (activeTab === "free") {
      result = result.filter(cb => !cb.price || Math.floor(parseFloat(cb.price?.toString() ?? "0")) === 0);
    }

    // Sort
    return result.sort((a, b) => {
      if (sortBy === "price") {
        const priceA = Math.floor(parseFloat(a.price?.toString() ?? "0"));
        const priceB = Math.floor(parseFloat(b.price?.toString() ?? "0"));
        return priceA - priceB;
      }
      return a.name.localeCompare(b.name);
    });
  }, [cardBacks, searchQuery, activeTab, sortBy]);

  // Buy handler for paid card backs
  const handleBuy = (cardBack: CardBack) => {
    setBuying(cardBack);
    setBuyError(null);
  };

  // Handle adding free card backs
  const handleAddFreeCardBack = async (cardBack: CardBack) => {
    try {
      let apiUrl = `/api/card-backs/purchase/${cardBack.id}`;
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();
      
      if (!res.ok) {
        toast({
          title: "Error",
          description: data.error ?? "Failed to add card back",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Success!",
        description: `${cardBack.name} has been added to your collection.`,
      });
      
      refetch();
      queryClient.invalidateQueries({ queryKey: ["/api/card-backs/my-card-backs"] });
    } catch (error) {
      console.error("Error adding free card back:", error);
      toast({
        title: "Error",
        description: "Failed to add card back to your collection",
        variant: "destructive"
      });
    }
  };

  // Confirm purchase
  const confirmBuy = async () => {
    if (!buying) return;
    setIsBuying(true);
    setBuyError(null);
    try {
      let apiUrl = `/api/card-backs/purchase/${buying.id}`;
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();
      
      if (!res.ok) {
        setBuyError(data.error ?? "Purchase failed");
        setIsBuying(false);
        return;
      }

      toast({
        title: "Purchase successful!",
        description: `You've acquired ${buying.name}.`,
      });
      
      setBuying(null);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["/api/currency/balance"] });
    } catch (e: any) {
      console.error("Purchase error:", e);
      setBuyError(e.message ?? "Purchase failed");
    } finally {
      setIsBuying(false);
    }
  };

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    await refetch();
    await queryClient.invalidateQueries({ queryKey: ["/api/currency/balance"] });
    return Promise.resolve();
  };

  return (
   
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black text-white pb-20">
        <EnhancedMarketplaceHeader title="Card Back Marketplace" backPath="/shop?tab=marketplace" />
        <PullToRefresh 
      onRefresh={handleRefresh}
      pullingContent={
        <div className="text-center p-2">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
        </div>
      }
      refreshingContent={
        <div className="text-center p-4">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Refreshing...</p>
        </div>
      }
    >
        
       <div>

       <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex flex-col gap-6">
            {/* Filters and controls */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="bg-black/30">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="premium">Premium</TabsTrigger>
                    <TabsTrigger value="standard">Standard</TabsTrigger>
                    <TabsTrigger value="free">Free</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-grow">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search card backs..." 
                    className="pl-10 bg-black/30 border-primary/30 focus:border-primary" 
                    value={searchQuery} 
                    onChange={e => setSearchQuery(e.target.value)} 
                  />
                </div>
                
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="bg-black/30">
                        <Filter className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-gray-900 border-primary/30">
                      <DropdownMenuItem onClick={() => setSortBy("name")}>
                        Sort by Name
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy("price")}>
                        Sort by Price
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button variant="outline" size="icon" className="bg-black/30" onClick={() => setViewType(v => v === "grid" ? "list" : "grid")}>
                    {viewType === "grid" ? <List className="h-4 w-4" /> : <Grid3X3 className="h-4 w-4" />}
                  </Button>

                  <Button variant="outline" size="icon" className="bg-black/30" onClick={() => refetch()}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Card grid */}
            <motion.div 
              className={cn(
                "grid gap-4",
                viewType === "grid" ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4" : "grid-cols-1"
              )} 
              variants={containerVariants} 
              initial="hidden" 
              animate="visible"
            >
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <LoadingCardBack key={i} />
                ))
              ) : filteredAndSortedCardBacks.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center gap-4 py-12 text-center">
                  <Package className="h-12 w-12 text-gray-500" />
                  <div>
                    <p className="text-lg font-medium text-gray-300">No card backs found</p>
                    <p className="text-sm text-gray-500">Try adjusting your filters or search terms</p>
                  </div>
                </div>
              ) : (
                filteredAndSortedCardBacks.map(cardBack => (
                  <CardBackCard
                    key={cardBack.id}
                    cardBack={cardBack}
                    onBuy={() => {
                      if (!cardBack.price || Math.floor(parseFloat(cardBack.price?.toString() ?? "0")) === 0) {
                        handleAddFreeCardBack(cardBack);
                      } else {
                        handleBuy(cardBack);
                      }
                    }}
                    onMagnify={() => setMagnified(cardBack)}
                  />
                ))
              )}
            </motion.div>
          </div>
        </div>

        {/* Magnify dialog */}
        <Dialog open={!!magnified} onOpenChange={open => !open && setMagnified(null)}>
          <DialogContent className="bg-black border-primary/30 max-w-xs w-full flex flex-col items-center">
            {magnified && (
              <>
                <img
                  src={getCardBackImageUrl(magnified.imageUrl)}
                  alt={magnified.name}
                  className="w-48 h-72 object-contain mb-4 rounded shadow-lg border border-primary/30 bg-black"
                  onError={e => (e.currentTarget as HTMLImageElement).src = "/assets/images/default-card-back.svg"}
                />
                <div className="text-lg font-bold mb-1">{magnified.name}</div>
                <div className="text-sm text-gray-400 mb-2">{magnified.description}</div>
                <div className="flex items-center gap-2">
                  {(!magnified.price || Math.floor(parseFloat(magnified.price?.toString() ?? "0")) === 0) ? (
                    <span className="text-green-400 font-semibold">Free</span>
                  ) : (
                    <>
                      {magnified.currencyType === "premium" ? <Gem className="h-4 w-4 text-purple-400" /> : <Coins className="h-4 w-4 text-amber-400" />}
                      <span className="text-white font-medium">{Math.floor(parseFloat(magnified.price?.toString() ?? "0"))}</span>
                    </>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
        {/* Buy dialog for paid card backs */}
        <Dialog open={!!buying} onOpenChange={open => !open && setBuying(null)}>
          <DialogContent className="bg-black border-primary/30 max-w-xs w-full flex flex-col items-center">
            {buying && (
              <>
                <img
                  src={getCardBackImageUrl(buying.imageUrl)}
                  alt={buying.name}
                  className="w-40 h-60 object-contain mb-4 rounded shadow-lg border border-primary/30 bg-black"
                  onError={e => (e.currentTarget as HTMLImageElement).src = "/assets/images/default-card-back.svg"}
                />
                <div className="text-lg font-bold mb-1">{buying.name}</div>
                <div className="text-sm text-gray-400 mb-2">{buying.description}</div>
                <div className="flex items-center gap-2 mb-4">
                  {buying.currencyType === "premium" ? <Gem className="h-4 w-4 text-purple-400" /> : <Coins className="h-4 w-4 text-amber-400" />}
                  <span className="text-white font-medium text-lg">{Math.floor(parseFloat(buying.price?.toString() ?? "0"))}</span>
                </div>
                <div className="w-full flex flex-col gap-2 mb-2">
                  <div className="flex justify-between text-sm">
                    <span>Your balance:</span>
                    <span className={buying.currencyType === "premium" ? "text-purple-400" : "text-amber-400"}>
                      {buying.currencyType === "premium" ? balance?.premiumCurrency ?? 0 : balance?.currency ?? 0}
                    </span>
                  </div>
                  {buyError && (
                    <div className="bg-red-900/20 border border-red-700/40 text-red-300 p-2 rounded-md text-xs flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {buyError}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 w-full mt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setBuying(null)} disabled={isBuying}>Cancel</Button>
                  <Button className="flex-1 bg-green-700 hover:bg-green-800 text-white" onClick={confirmBuy} disabled={isBuying}>
                    {isBuying ? "Processing..." : "Confirm Purchase"}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        
       </div>
        </PullToRefresh>

      </div>
  );
}
